import { SessionService, type SessionFile } from "@/shared/domain/sessions";
import { ErrorMonitoringService } from "@/shared/services";
import { Datasource } from "@/shared/state/visualization";

import {
  type VisualizerInitContext,
  type VisualizerNotice,
} from "./visualizerInit";

/**
 * Page size requested for every request in a session's file listing.
 *
 * The route accepts a `page_size` up to 1000 and rejects anything larger; left
 * unset it defaults to 100, so asking for the maximum cuts the round trips on a
 * large session tenfold.
 */
const SESSION_FILE_PAGE_SIZE = 1000;

/**
 * The outcome of listing one session named in the URL: the files it holds, or a
 * marker that the listing failed.
 *
 * A session whose listing comes back empty is `ok` with an empty array rather
 * than a failure: the listing omits files that are reserved or being deleted,
 * so a session with members can return none of them.
 */
export type SessionResolution =
  | { status: "ok"; sessionId: string; files: SessionFile[] }
  | { status: "failed"; sessionId: string };

/**
 * List every file in each session named by `?session=`, so the URL can hand
 * over a whole session instead of the files the user paged through one screen
 * at a time. Returns an empty array when the URL names no session.
 *
 * Sessions are listed concurrently. Every error a listing throws is captured
 * for error monitoring and becomes a `failed` resolution instead of
 * propagating, so one unreadable session does not cost the user the sessions
 * and datasources alongside it; `sessionNotices` turns it into a banner.
 */
export async function resolveSessions(
  ctx: VisualizerInitContext,
): Promise<SessionResolution[]> {
  const sessionIds = ctx.sessionIds ?? [];

  if (sessionIds.length === 0) {
    return [];
  }

  const sessionService = new SessionService(ctx.httpClient);

  return Promise.all(
    sessionIds.map(async (sessionId): Promise<SessionResolution> => {
      try {
        const files = await sessionService.listAllSessionFiles(sessionId, {
          limit: SESSION_FILE_PAGE_SIZE,
          resourceOwnerId: ctx.orgId,
        });
        return { status: "ok", sessionId, files };
      } catch (error) {
        ErrorMonitoringService.captureError(error);
        return { status: "failed", sessionId };
      }
    }),
  );
}

/**
 * One file datasource per file in each listed session, ready to merge with the
 * URL's own. A session that failed to list, or that holds no files, adds none.
 *
 * Each datasource carries `associationId`, the parent dataset the listing
 * reports for the file, and a `derivedFrom` marker naming the session it was
 * expanded from. The marker distinguishes a file that arrived with a session
 * from one the user picked: marked files are left out of the URL's `?file=`
 * params, so a session of any size costs one param, and the workspace events
 * request drops a marked file whose parent dataset it already names.
 */
export function sessionDatasources(
  resolutions: SessionResolution[],
): Datasource[] {
  return resolutions.flatMap((resolution) => {
    if (resolution.status !== "ok") {
      return [];
    }

    return resolution.files.map((file) => ({
      type: "file" as const,
      value: {
        fileId: file.file_id,
        associationId: file.dataset_id,
      },
      derivedFrom: {
        type: "session" as const,
        sessionId: resolution.sessionId,
      },
    }));
  });
}

/**
 * Banners raised for each session's own outcome: a listing that failed, or one
 * that succeeded with nothing in it. A session that came back with files raises
 * none.
 *
 * An empty session is worth saying out loud because the workspace it opens is
 * empty, and an empty visualizer with no explanation reads as a bug.
 */
export function sessionNotices(
  resolutions: SessionResolution[],
): VisualizerNotice[] {
  return resolutions.flatMap((resolution): VisualizerNotice[] => {
    if (resolution.status === "failed") {
      return [
        {
          name: "SessionFilesUnresolvedError",
          title: "Session not loaded",
          message: `Files in session '${resolution.sessionId}' could not be listed.`,
          severity: "error",
        },
      ];
    }

    if (resolution.files.length === 0) {
      return [
        {
          name: "SessionEmptyNotice",
          title: "Nothing to visualize",
          message: `Session '${resolution.sessionId}' has no files available to visualize.`,
          severity: "info",
        },
      ];
    }

    return [];
  });
}

/**
 * The banner shown when a shared workspace opens with a session in the URL: the
 * workspace restores its own data, so the session is not listed at all. Names
 * the workspace when its id is known.
 */
export function sessionDroppedForWorkspaceNotice(
  workspaceId: string | undefined,
  sessionIds: string[],
): VisualizerNotice {
  const workspace = workspaceId
    ? `workspace '${workspaceId}'`
    : "a saved workspace";
  const sessions = sessionIds.map((sessionId) => `'${sessionId}'`).join(", ");
  const sessionNoun = sessionIds.length === 1 ? "session" : "sessions";
  return {
    name: "SessionDroppedForWorkspaceNotice",
    title: "Session not loaded",
    message: `The URL includes both ${workspace} and ${sessionNoun} ${sessions}, but they are mutually exclusive. The workspace opened with its saved panels and data.`,
    severity: "info",
  };
}
